// MACHINERY V2 (Stroje) — the 13px form glyph set (M2.6).
//
// Spec: MACHINERY_V2_IMPLEMENTATION_TICKET.md §8 M2.6 ("teal glyph + mono
// eyebrow"). Reference impl: design_handoff_machinery_v3/machinery-sr.jsx
// lines 691-702 — the `_g` glyph props object and its eight paths, copied
// stroke-for-stroke. Artboards 0702 / 0703 (create) and 0700 / 0701 (detail).
//
// These are FORM glyphs, not icons: 13x13, 1.4 stroke, currentColor, aria-hidden.
// The label owns the accessible name — the glyph is decoration next to it.
// M2.7's CustomMachineDialog consumes the same set (the kit is shared with the
// Creative Designer) and EXTENDS it — see the "M2.7 additions" block at the
// bottom of this file.
//
// No Tailwind, no shadcn, no MUI, no @emotion, no sx, no dt tokens.
import React from "react";

const g = {
  width: 13,
  height: 13,
  viewBox: "0 0 13 13",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 1.4,
  strokeLinecap: "round" as const,
  strokeLinejoin: "round" as const,
  "aria-hidden": true,
};

export const DocGlyph: React.FC = () => (
  <svg {...g}>
    <path d="M3 1 L9 1 L11 3 L11 12 L3 12 Z" />
    <line x1="4.5" y1="5" x2="9.5" y2="5" />
    <line x1="4.5" y1="7.5" x2="9.5" y2="7.5" />
    <line x1="4.5" y1="10" x2="7.5" y2="10" />
  </svg>
);

export const NoteGlyph: React.FC = () => (
  <svg {...g}>
    <rect x="2" y="2" width="9" height="9" rx="1" />
    <line x1="4" y1="5" x2="9" y2="5" />
    <line x1="4" y1="7" x2="9" y2="7" />
    <line x1="4" y1="9" x2="7" y2="9" />
  </svg>
);

export const CalendarGlyph: React.FC = () => (
  <svg {...g}>
    <rect x="2" y="3" width="9" height="8" rx="1" />
    <line x1="2" y1="5.5" x2="11" y2="5.5" />
    <line x1="5" y1="2" x2="5" y2="4" />
    <line x1="8" y1="2" x2="8" y2="4" />
  </svg>
);

export const WrenchGlyph: React.FC = () => (
  <svg {...g}>
    <path d="M9.5 1.5 a2.5 2.5 0 0 0 -2.7 3.5 L2.4 9.4 a1.1 1.1 0 1 0 1.5 1.5 L8.3 6.5 A2.5 2.5 0 0 0 11.5 3.4 L9.9 5 L8.4 4.6 L8 3.1 Z" />
  </svg>
);

export const PaperclipGlyph: React.FC = () => (
  <svg {...g}>
    <path d="M10 5 L5.5 9.5 a2 2 0 1 1 -2.8 -2.8 L8.5 1 a3 3 0 0 1 4.2 4.2 L7 11" />
  </svg>
);

export const AlertGlyph: React.FC = () => (
  <svg {...g}>
    <path d="M6.5 1.5 L12 11 L1 11 Z" />
    <line x1="6.5" y1="5" x2="6.5" y2="7.5" />
    <circle cx="6.5" cy="9.2" r="0.4" fill="currentColor" />
  </svg>
);

export const BangGlyph: React.FC = () => (
  <svg {...g}>
    <circle cx="6.5" cy="6.5" r="5" />
    <line x1="6.5" y1="3.5" x2="6.5" y2="7.5" />
    <circle cx="6.5" cy="9.4" r="0.4" fill="currentColor" />
  </svg>
);

export const CheckGlyph: React.FC = () => (
  <svg {...g} strokeWidth={1.6}>
    <path d="M2.5 6.5 L5.5 9.5 L11 4" />
  </svg>
);

export const ShareGlyph: React.FC = () => (
  <svg {...g}>
    <circle cx="3.5" cy="6.5" r="1.5" />
    <circle cx="9.5" cy="3" r="1.5" />
    <circle cx="9.5" cy="10" r="1.5" />
    <line x1="4.8" y1="5.7" x2="8.2" y2="3.7" />
    <line x1="4.8" y1="7.3" x2="8.2" y2="9.3" />
  </svg>
);

export const PdfGlyph: React.FC = () => (
  <svg {...g}>
    <path d="M3 1 L9 1 L11 3 L11 12 L3 12 Z" />
    <line x1="4.8" y1="8.6" x2="9.2" y2="8.6" />
    <line x1="4.8" y1="10.4" x2="7.6" y2="10.4" />
  </svg>
);

export const EditGlyph: React.FC = () => (
  <svg {...g}>
    <path d="M2 11 L3 8.5 L9.5 2 L11.5 4 L5 10.5 Z" />
  </svg>
);

export const ClockGlyph: React.FC = () => (
  <svg {...g}>
    <circle cx="6.5" cy="6.5" r="5" />
    <path d="M6.5 3.4 V6.6 L8.8 8" />
  </svg>
);

// ─── M2.7 additions (add-machine) ────────────────────────────────────
// Copied stroke-for-stroke from design_handoff_machinery_v3/machinery-catalog
// .jsx lines 20-31 (its `_cg` local glyph block), which declares itself "same
// grammar as SR glyphs" — so they belong in THIS set rather than in a second
// one. Artboards 0801-0804. `CWrenchGlyph` and `CCalGlyph` from that block are
// NOT repeated here: they are byte-identical to WrenchGlyph / CalendarGlyph
// above. The location pin is `PinGlyph` from fleet/fleetCardAtoms.tsx — the
// designer uses that same 11px shared pin inside its Lokácia FormField.

export const ImageGlyph: React.FC = () => (
  <svg {...g}>
    <rect x="1.5" y="2" width="10" height="9" rx="1" />
    <circle cx="4.5" cy="5" r="1" />
    <path d="M1.5 9.5 L4.5 7 L6.5 8.5 L9 6 L11.5 8.5" />
  </svg>
);

export const MachineTypeGlyph: React.FC = () => (
  <svg {...g}>
    <rect x="4.5" y="1.5" width="4" height="3" rx="0.8" />
    <rect x="1" y="8.5" width="4" height="3" rx="0.8" />
    <rect x="8" y="8.5" width="4" height="3" rx="0.8" />
    <path d="M6.5 4.5 L6.5 6.5 M6.5 6.5 L3 6.5 L3 8.5 M6.5 6.5 L10 6.5 L10 8.5" />
  </svg>
);

export const TagGlyph: React.FC = () => (
  <svg {...g}>
    <path d="M1.5 1.5 L6 1.5 L11.5 7 L7 11.5 L1.5 6 Z" />
    <circle cx="4.2" cy="4.2" r="0.9" />
  </svg>
);

export const BoxGlyph: React.FC = () => (
  <svg {...g}>
    <path d="M6.5 1.5 L11.5 4 L11.5 9 L6.5 11.5 L1.5 9 L1.5 4 Z" />
    <path d="M1.5 4 L6.5 6.5 L11.5 4 M6.5 6.5 L6.5 11.5" />
  </svg>
);

export const GearGlyph: React.FC = () => (
  <svg {...g}>
    <circle cx="6.5" cy="6.5" r="2" />
    <path d="M6.5 1.2 L6.5 2.8 M6.5 10.2 L6.5 11.8 M1.2 6.5 L2.8 6.5 M10.2 6.5 L11.8 6.5 M2.7 2.7 L3.9 3.9 M9.1 9.1 L10.3 10.3 M10.3 2.7 L9.1 3.9 M3.9 9.1 L2.7 10.3" />
  </svg>
);

export const AxisGlyph: React.FC = () => (
  <svg {...g}>
    <path d="M2 11 L2 2 M2 11 L11 11" />
    <path d="M2 2 L3 3.5 M2 2 L1 3.5 M11 11 L9.5 10 M11 11 L9.5 12" />
    <path d="M4.5 8.5 L8.5 4.5" />
  </svg>
);

export const HashGlyph: React.FC = () => (
  <svg {...g}>
    <line x1="4.5" y1="1.5" x2="3.5" y2="11.5" />
    <line x1="9.5" y1="1.5" x2="8.5" y2="11.5" />
    <line x1="1.5" y1="4.5" x2="11.5" y2="4.5" />
    <line x1="1.5" y1="8.5" x2="11.5" y2="8.5" />
  </svg>
);

export const CpuGlyph: React.FC = () => (
  <svg {...g}>
    <rect x="3" y="3" width="7" height="7" rx="1" />
    <rect x="5.2" y="5.2" width="2.6" height="2.6" />
    <path d="M5 1.5 L5 3 M8 1.5 L8 3 M5 10 L5 11.5 M8 10 L8 11.5 M1.5 5 L3 5 M1.5 8 L3 8 M10 5 L11.5 5 M10 8 L11.5 8" />
  </svg>
);

export const ListGlyph: React.FC = () => (
  <svg {...g}>
    <rect x="1.5" y="2" width="10" height="9" rx="1" />
    <line x1="3.5" y1="4.5" x2="9.5" y2="4.5" />
    <line x1="3.5" y1="6.5" x2="9.5" y2="6.5" />
    <line x1="3.5" y1="8.5" x2="7" y2="8.5" />
  </svg>
);
